import autobind from "autobind-decorator";
import {computed, observable, action} from "mobx";
import { Query, FetchFunction } from "./types";
import {logger} from "../../../../common/utils/logger/index";
import {cloneDeepSafe} from "../../../../common/utils/mobx";

interface State<T> {

  items: T[];
  count: number;
  dropdown: string;
  limit: number;
  page: number;

  search: string;
  sortKey: string;
  sortDirection: 1 | -1;
  filtersActive: {
    [key: string]: {
      $in: string[];
    };
  };

  loading: boolean;
  error: string;

}

@autobind
export class Store<T> {

  _fetch: FetchFunction;

  @observable s: State<T> = {
    items: [],
    count: 0,
    dropdown: "",
    limit: 10,
    page: 1,

    search: "",
    sortKey: "created",
    sortDirection: -1,
    filtersActive: {},

    loading: true,
    error: "",
  };

  constructor(fetch: FetchFunction) {
    this._fetch = fetch;
  }

  @computed get totalPages() {
    return Math.ceil((this.s.count || 0) / this.s.limit);
  }

  @action update = (data: Partial<State<T>>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof State<T>];
        if (value !== undefined) {
          this.s[key as keyof State<T>] = value;
        }
      }
    }
  }

  @action fetch = async () => {
    this.update({ loading: true, error: "", dropdown: "" });
    try {

      const { page, limit, search, sortDirection, sortKey, filtersActive } = this.s;

      const filters = cloneDeepSafe(filtersActive);
      for (const key in filters) {
        if (filters.hasOwnProperty(key) && filters[key].$in.length === 0) {
          delete filters[key];
        }
      }

      // tslint:disable-next-line
      await this._fetch({
        page: page,
        limit: limit,
        search: search,
        sort: { [sortKey]: sortDirection },
        query: filters,
      });

      this.update({
        loading: false,
      });

    }
    catch (e) {
      logger.captureException(e);
      this.update({
        loading: false,
        error: "Error loading items, try again or contact us",
      });
    }
  }

  @action reset = () => {
    this.update({
      search: "",
      sortKey: "created",
      sortDirection: -1,
      filtersActive: {},
    });
    this.fetch();
  }

  @action setPage = (page: number) => {
    this.s.page = page;
    this.fetch();
  }

  @action filterToggle = (key: string, value: string) => {
    if (this.s.filtersActive[key]) {
      const index = this.s.filtersActive[key].$in.indexOf(value);
      if (index === -1)
        this.s.filtersActive[key].$in.push(value);
      else
        this.s.filtersActive[key].$in.splice(index, 1);
    }
    else {
      this.s.filtersActive[key] = { $in: [value] };
    }
  }

  filterIsActive = (key: string, value: string) => {
    if (this.s.filtersActive[key]) {
      const index = this.s.filtersActive[key].$in.indexOf(value);
      return index !== -1;
    }
    else {
      return false;
    }
  }

}
